import { effect, ElementRef, inject, input, Renderer2, signal } from '@angular/core';
import { UiDragDropModule } from '@common/ui/frontend-shared/dragdrop';
import { UiIconComponent } from '@common/ui/frontend-shared/icons';
import { TimelineService } from './timeline.service';
import { AUTO_SNAP_DISTANCE, MIN_ITEM_LENGTH } from './constants';
import * as i0 from "@angular/core";
import * as i1 from "../../../../../../ui/frontend-shared/src/lib/dragdrop/draggable.directive";
export let MediaAssetTimelineTrackItemComponent = /*#__PURE__*/(() => {
  class MediaAssetTimelineTrackItemComponent {
    constructor() {
      this.item = input.required(...(ngDevMode ? [{
        debugName: "item"
      }] : /* istanbul ignore next */[]));
      this.timelineLength = input.required(...(ngDevMode ? [{
        debugName: "timelineLength"
      }] : /* istanbul ignore next */[]));
      this.el = inject(ElementRef);
      this.renderer = inject(Renderer2);
      this.timeline = inject(TimelineService);
      this.draggingMode = signal(null, ...(ngDevMode ? [{
        debugName: "draggingMode"
      }] : /* istanbul ignore next */[]));
      this._startPos = effect(() => this.updateStartPosition(), ...(ngDevMode ? [{
        debugName: "_startPos"
      }] : /* istanbul ignore next */[]));
      this._endPos = effect(() => this.updateEndPosition(), ...(ngDevMode ? [{
        debugName: "_endPos"
      }] : /* istanbul ignore next */[]));
      // only set while dragging!
      this.draggingContext = null;
    }
    onClick(e) {
      this.timeline.selectItem(this.item());
    }
    onDragStart(type, e) {
      e.dataTransfer.setData('text/plain', 'anything');
      this.timeline.setIsDragging(true);
      this.enterPixelPositionMode();
      this.draggingMode.set(type);
      const item = this.item();
      const siblings = this.timeline.getItemSiblings(item);
      // calculate the boundaries in which dragging is allowed
      const duration = this.timeline.duration();
      let minSec = 0,
        maxSec = duration;
      if (type === 'end') {
        minSec = item.start + MIN_ITEM_LENGTH;
        if (siblings.nextItem) {
          maxSec = siblings.nextItem.start;
        }
      } else if (type === 'start') {
        maxSec = item.end - MIN_ITEM_LENGTH;
        if (siblings.prevItem) {
          minSec = siblings.prevItem.end;
        }
      } else if (type === 'move') {
        minSec = 0;
        maxSec = duration - (item.end - item.start);
      }
      if (minSec < 0) minSec = 0;
      if (maxSec > duration) maxSec = duration;
      const minXPos = this.timeline.timelineSecondsToPxPosition(minSec).offsetX;
      const maxXPos = this.timeline.timelineSecondsToPxPosition(maxSec).offsetX;
      this.draggingContext = {
        ...siblings,
        minSec,
        maxSec,
        minXPos,
        maxXPos
      };
    }
    getDraggingInfo() {
      const what = this.draggingMode();
      const el = this.el.nativeElement;
      const rect = el.getBoundingClientRect();
      const left = el.offsetLeft + this.timeline.timelinePadding();
      const right = left + rect.width;
      const startSec = this.timeline.pxPositionToTimelineSeconds(left);
      const endSec = this.timeline.pxPositionToTimelineSeconds(right);
      return {
        what,
        left,
        right,
        startSec,
        endSec
      };
    }
    onDrag(e) {
      const {
        what,
        left,
        right,
        startSec,
        endSec
      } = this.getDraggingInfo();
      const el = this.el.nativeElement;
      if (what === 'move') {
        const requestedPos = el.offsetLeft + e.deltaX;
        const maxPos = this.draggingContext.maxXPos - this.timeline.timelinePadding();
        const minPos = this.draggingContext.minXPos - this.timeline.timelinePadding();
        const newPos = Math.max(minPos, Math.min(maxPos, requestedPos));
        this.renderer.setStyle(el, 'left', newPos + 'px');
      } else if (what === 'end') {
        const requestedPos = right + e.deltaX; // we'll need to calculate width, but thinking in absolute offset will help during calculation
        const maxPos = this.draggingContext.maxXPos;
        const minPos = this.draggingContext.minXPos;
        const newPos = Math.max(minPos, Math.min(maxPos, requestedPos));
        this.renderer.setStyle(el, 'width', newPos - left + 'px');
      } else if (what === 'start') {
        const requestedPos = el.offsetLeft + e.deltaX;
        const maxPos = this.draggingContext.maxXPos - this.timeline.timelinePadding();
        const minPos = this.draggingContext.minXPos - this.timeline.timelinePadding();
        const newPos = Math.max(minPos, Math.min(maxPos, requestedPos));
        const itemEnd = this.timeline.timelineSecondsToPxPosition(this.item().end).offsetX - this.timeline.timelinePadding();
        this.renderer.setStyle(el, 'left', newPos + 'px');
        this.renderer.setStyle(el, 'width', itemEnd - newPos + 'px');
      }
    }
    onDragEnd(e) {
      const {
        what,
        startSec,
        endSec
      } = this.getDraggingInfo();
      const item = this.item();
      const trackId = item.track;
      let invalidPosition = false;
      if (what === 'move') {
        const itemAtStart = this.timeline.getItemAtPosition(startSec.timelineSeconds, trackId);
        if (itemAtStart && itemAtStart.start < startSec.timelineSeconds && itemAtStart.end > endSec.timelineSeconds) invalidPosition = true;
      }
      if (!invalidPosition) {
        let start = startSec.timelineSeconds;
        let end = endSec.timelineSeconds;
        // auto-snap to sibling if there is one super close by
        if (what === 'move') {
          const nearbyStartItem = this.timeline.getItemAtPosition(start - AUTO_SNAP_DISTANCE, trackId);
          if (nearbyStartItem && nearbyStartItem.id !== item.id) start = nearbyStartItem.end;
          const nearbyEndItem = this.timeline.getItemAtPosition(end + AUTO_SNAP_DISTANCE, trackId);
          if (nearbyEndItem && nearbyEndItem.id !== item.id) end = nearbyEndItem.start;
        } else {
          const siblings = this.timeline.getItemSiblings(item);
          if (what === 'start') {
            if (siblings.prevItem && start - siblings.prevItem.end <= AUTO_SNAP_DISTANCE) start = siblings.prevItem.end;
          } else if (what === 'end') {
            if (siblings.nextItem && siblings.nextItem.start - end <= AUTO_SNAP_DISTANCE) end = siblings.nextItem.start;
          }
        }
        this.timeline.updateItem(item.id, {
          start,
          end
        });
      }
      this.draggingMode.set(null);
      this.endPixelPositionMode();
      this.timeline.setIsDragging(false);
    }
    enterPixelPositionMode() {
      const el = this.el.nativeElement;
      const rect = el.getBoundingClientRect();
      const left = el.offsetLeft;
      this.renderer.setStyle(el, 'left', left + 'px');
      this.renderer.setStyle(el, 'right', 'initial');
      this.renderer.setStyle(el, 'width', rect.width + 'px');
    }
    endPixelPositionMode() {
      this.updateStartPosition();
      this.updateEndPosition();
    }
    updateStartPosition() {
      const pos = this.item().start / this.timelineLength();
      if (this.draggingMode() !== null) return;
      this.renderer.setStyle(this.el.nativeElement, 'left', pos * 100 + '%');
    }
    updateEndPosition() {
      const pos = (this.timelineLength() - this.item().end) / this.timelineLength();
      if (this.draggingMode() !== null) return;
      this.renderer.setStyle(this.el.nativeElement, 'right', pos * 100 + '%');
    }
    static {
      this.ɵfac = function MediaAssetTimelineTrackItemComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MediaAssetTimelineTrackItemComponent)();
      };
    }
    static {
      this.ɵcmp = /*@__PURE__*/i0.ɵɵdefineComponent({
        type: MediaAssetTimelineTrackItemComponent,
        selectors: [["media-asset-timeline-track-item"]],
        hostVars: 6,
        hostBindings: function MediaAssetTimelineTrackItemComponent_HostBindings(rf, ctx) {
          if (rf & 2) {
            i0.ɵɵclassProp("start-connected", ctx.item().startConnected)("end-connected", ctx.item().endConnected)("selected", ctx.item().selected);
          }
        },
        inputs: {
          item: [1, "item"],
          timelineLength: [1, "timelineLength"]
        },
        decls: 6,
        vars: 7,
        consts: [["uiDraggable", "", "ghostMode", "hide", 1, "drag-handle-start", 3, "onDragStart", "onDrag", "onDragEnd", "uiDraggableId", "uiDragDropScope"], ["icon", "bars"], ["uiDraggable", "", "ghostMode", "hide", 1, "drag-handle-center", "content", 3, "onDragStart", "onDrag", "onDragEnd", "click", "uiDraggableId", "uiDragDropScope"], ["uiDraggable", "", "ghostMode", "hide", 1, "drag-handle-end", 3, "onDragStart", "onDrag", "onDragEnd", "uiDraggableId", "uiDragDropScope"]],
        template: function MediaAssetTimelineTrackItemComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵelementStart(0, "button", 0);
            i0.ɵɵlistener("onDragStart", function MediaAssetTimelineTrackItemComponent_Template_button_onDragStart_0_listener($event) {
              return ctx.onDragStart("start", $event);
            })("onDrag", function MediaAssetTimelineTrackItemComponent_Template_button_onDrag_0_listener($event) {
              return ctx.onDrag($event);
            })("onDragEnd", function MediaAssetTimelineTrackItemComponent_Template_button_onDragEnd_0_listener($event) {
              return ctx.onDragEnd($event);
            });
            i0.ɵɵelement(1, "ui-icon", 1);
            i0.ɵɵelementEnd();
            i0.ɵɵelementStart(2, "div", 2);
            i0.ɵɵlistener("onDragStart", function MediaAssetTimelineTrackItemComponent_Template_div_onDragStart_2_listener($event) {
              return ctx.onDragStart("move", $event);
            })("onDrag", function MediaAssetTimelineTrackItemComponent_Template_div_onDrag_2_listener($event) {
              return ctx.onDrag($event);
            })("onDragEnd", function MediaAssetTimelineTrackItemComponent_Template_div_onDragEnd_2_listener($event) {
              return ctx.onDragEnd($event);
            })("click", function MediaAssetTimelineTrackItemComponent_Template_div_click_2_listener($event) {
              return ctx.onClick($event);
            });
            i0.ɵɵtext(3);
            i0.ɵɵelementEnd();
            i0.ɵɵelementStart(4, "button", 3);
            i0.ɵɵlistener("onDragStart", function MediaAssetTimelineTrackItemComponent_Template_button_onDragStart_4_listener($event) {
              return ctx.onDragStart("end", $event);
            })("onDrag", function MediaAssetTimelineTrackItemComponent_Template_button_onDrag_4_listener($event) {
              return ctx.onDrag($event);
            })("onDragEnd", function MediaAssetTimelineTrackItemComponent_Template_button_onDragEnd_4_listener($event) {
              return ctx.onDragEnd($event);
            });
            i0.ɵɵelement(5, "ui-icon", 1);
            i0.ɵɵelementEnd();
          }
          if (rf & 2) {
            i0.ɵɵproperty("uiDraggableId", ctx.item().id + "-start")("uiDragDropScope", "track");
            i0.ɵɵadvance(2);
            i0.ɵɵproperty("uiDraggableId", ctx.item().id + "-move")("uiDragDropScope", "track");
            i0.ɵɵadvance();
            i0.ɵɵtextInterpolate1(" ", ctx.item().label, "\n");
            i0.ɵɵadvance();
            i0.ɵɵproperty("uiDraggableId", ctx.item().id + "-end")("uiDragDropScope", "track");
          }
        },
        dependencies: [UiDragDropModule, i1.Draggable, UiIconComponent],
        styles: ["[_nghost-%COMP%]{--hoverColor:var(--greyColor)}[_nghost-%COMP%]{display:flex;background:var(--softGreyColor);position:absolute;padding:0;border-radius:5px;height:100%}[_nghost-%COMP%]{container-name:trackitem;container-type:inline-size}.selected[_nghost-%COMP%]{--hoverColor:var(--primaryColor)}.selected[_nghost-%COMP%]{background:var(--primaryDarkenedColor)}.end-connected[_nghost-%COMP%]{border-right:1px solid var(--greyColor);border-top-right-radius:0;border-bottom-right-radius:0}.start-connected[_nghost-%COMP%]{border-left:1px solid var(--greyColor);border-top-left-radius:0;border-bottom-left-radius:0}[_nghost-%COMP%]   .content[_ngcontent-%COMP%]{flex:1;overflow:hidden;text-overflow:ellipsis;padding:5px 10px;white-space:nowrap}.drag-handle-center[_ngcontent-%COMP%]{transition:background-color .2s}.drag-handle-center[_ngcontent-%COMP%]:hover{cursor:grab;background:var(--hoverColor)}.drag-handle-start[_ngcontent-%COMP%], .drag-handle-end[_ngcontent-%COMP%]{transition:background-color .2s,opacity .2s;border:0;background:0;color:var(--textColor)}.drag-handle-start[_ngcontent-%COMP%], .drag-handle-end[_ngcontent-%COMP%]{opacity:.5}.drag-handle-start[_ngcontent-%COMP%]:hover, .drag-handle-end[_ngcontent-%COMP%]:hover{cursor:grab;opacity:1;background:var(--hoverColor)}.drag-handle-start[_ngcontent-%COMP%]{padding-left:2px}.drag-handle-end[_ngcontent-%COMP%]{padding-right:2px}@container trackitem (max-width: 100px){.drag-handle-start[_ngcontent-%COMP%]{padding-right:2px}.drag-handle-end[_ngcontent-%COMP%]{padding-left:2px}.content[_ngcontent-%COMP%]{padding-left:0!important;padding-right:0!important}}"],
        changeDetection: 0
      });
    }
  }
  return MediaAssetTimelineTrackItemComponent;
})();